import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../../../axios-client';
import { useNavigate } from 'react-router-dom';
import { SearchIcon } from '@chakra-ui/icons';
import { FaPlay } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import MyPagination from '../../../components/MyPagination';
import { useStateContext } from '../../../context/ContextProvider';
import {
    Flex,
    Box,
    Button,
    InputGroup,
    InputLeftElement,
    Input,
    useColorModeValue,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Text,
    SimpleGrid,
    Card,
    CardBody,
    CardFooter,
    Image,
    Heading,
    Skeleton,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    AspectRatio,
    Badge,
    Icon,
    Stack
} from '@chakra-ui/react';

export const Tutoriels = () => {
    const { t } = useTranslation();
    const [loadingData, setLoadingData] = useState(true);
    const navigate = useNavigate();

    // Search and pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(6); // 6 cards per page
    const [paginationData, setPaginationData] = useState(null);

    // List data & active video modal
    const [data, setData] = useState([]);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [activeVideoId, setActiveVideoId] = useState(null);
    const [activeVideoTitle, setActiveVideoTitle] = useState('');

    // Description Modal disclosure & content state
    const { isOpen: isDescOpen, onOpen: onDescOpen, onClose: onDescClose } = useDisclosure();
    const [descModalTitle, setDescModalTitle] = useState('');
    const [descModalContent, setDescModalContent] = useState('');

    const getYouTubeId = (url) => {
        if (!url) return null;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    // Load data
    const fetchTutoriels = useCallback(() => {
        setLoadingData(true);
        axiosClient.get(`/tutoriels/getAllTutoriels/?page_size=${pageSize}&page=${currentPage + 1}&search_term=${searchTerm}`)
            .then((response) => {
                setPaginationData(response.data);
                setData(response.data.results || []);
                setLoadingData(false);
            })
            .catch(() => {
                setLoadingData(false);
            });
    }, [pageSize, currentPage, searchTerm]);

    useEffect(() => {
        fetchTutoriels();
    }, [fetchTutoriels]);

    const handleWatchVideo = (url, title) => {
        const videoId = getYouTubeId(url);
        if (videoId) {
            setActiveVideoId(videoId);
            setActiveVideoTitle(title);
            onOpen();
        } else {
            // Fallback: open in new tab if regex parsing failed
            window.open(url, '_blank');
        }
    };

    const handleCloseModal = () => {
        setActiveVideoId(null);
        setActiveVideoTitle('');
        onClose();
    };

    // Colors
    const cardBg = useColorModeValue('white', 'gray.800');
    const textColor = useColorModeValue('gray.600', 'gray.300');

    return (
        <Box position="relative" mb={5}>
            {/* Breadcrumbs */}
            <Flex>
                <Box w="100%" display="flex" justifyContent="space-between" flexDirection={{ base: 'column', md: "row" }} mt={2} mb={{ base: 0, md: 5 }}>
                    <Breadcrumb fontFamily='Plus Jakarta Display' fontStyle="italic" fontSize={{ base: "20px", sm: "25px", lg: "27px" }} color="gray">
                        <BreadcrumbItem>
                            <BreadcrumbLink onClick={() => navigate('/')}>{t("Accueil")}</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbItem isCurrentPage>
                            <BreadcrumbLink>{t('Tutoriels')}</BreadcrumbLink>
                        </BreadcrumbItem>
                    </Breadcrumb>
                </Box>
            </Flex>

            {/* Filter / Search header */}
            <Flex
                mb={6}
                p={4}
                bg={useColorModeValue('white', 'gray.700')}
                rounded="2xl"
                align="center"
                justify="space-between"
                shadow="sm"
                direction={{ base: 'column', md: 'row' }}
                gap={4}
            >
                <Text fontSize="lg" fontWeight="bold">
                    {t("Apprendre à utiliser Ecolinke")}
                </Text>
                <InputGroup size="md" w={{ base: '100%', md: "300px" }}>
                    <InputLeftElement pointerEvents='none'>
                        <SearchIcon color='gray.300' />
                    </InputLeftElement>
                    <Input
                        borderRadius="xl"
                        placeholder={t("Recherche...")}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(0);
                        }}
                    />
                </InputGroup>
            </Flex>

            {/* Grid of Tutorial cards */}
            {loadingData ? (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                    {[1, 2, 3].map((i) => (
                        <Box key={i} p={4} bg={cardBg} borderRadius="2xl" shadow="sm">
                            <Skeleton height="180px" borderRadius="xl" mb={4} />
                            <Skeleton height="24px" width="70%" mb={2} />
                            <Skeleton height="16px" width="90%" mb={4} />
                            <Skeleton height="36px" width="100px" />
                        </Box>
                    ))}
                </SimpleGrid>
            ) : data.length > 0 ? (
                <Stack spacing={6}>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                        {data.map((tuto) => {
                            const videoId = getYouTubeId(tuto.youtube_link);
                            const thumbnailUrl = videoId 
                                ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` 
                                : `https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?q=80&w=600&auto=format&fit=crop`;

                            return (
                                <Card
                                    key={tuto.id}
                                    bg={cardBg}
                                    borderRadius="2xl"
                                    overflow="hidden"
                                    shadow="sm"
                                    transition="all 0.3s"
                                    _hover={{ transform: 'translateY(-4px)', shadow: 'md' }}
                                >
                                    <Box position="relative" cursor="pointer" onClick={() => handleWatchVideo(tuto.youtube_link, tuto.title)}>
                                        <Image
                                            src={thumbnailUrl}
                                            alt={tuto.title}
                                            h="180px"
                                            w="100%"
                                            objectFit="cover"
                                        />
                                        <Flex
                                            position="absolute"
                                            top="0"
                                            left="0"
                                            w="100%"
                                            h="100%"
                                            bg="blackAlpha.400"
                                            align="center"
                                            justify="center"
                                            opacity="0"
                                            transition="all 0.3s"
                                            _hover={{ opacity: 1 }}
                                        >
                                            <CirclePlayButton />
                                        </Flex>
                                        <Badge
                                            position="absolute"
                                            bottom={2}
                                            right={2}
                                            colorScheme="purple"
                                            borderRadius="md"
                                            px={2}
                                        >
                                            YouTube
                                        </Badge>
                                    </Box>
                                    <CardBody pb={2}>
                                        <Heading size="md" mb={2} isTruncated>
                                            {tuto.title}
                                        </Heading>
                                        <Text fontSize="sm" color={textColor} noOfLines={3}>
                                            {tuto.description || t("Aucune description disponible pour ce tutoriel.")}
                                        </Text>
                                        {tuto.description && (
                                            <Button
                                                size="xs"
                                                variant="link"
                                                colorScheme="purple"
                                                mt={2}
                                                onClick={() => {
                                                    setDescModalTitle(tuto.title);
                                                    setDescModalContent(tuto.description);
                                                    onDescOpen();
                                                }}
                                            >
                                                {t("Voir plus")}
                                            </Button>
                                        )}
                                    </CardBody>
                                    <CardFooter pt={2}>
                                        <Button
                                            leftIcon={<Icon as={FaPlay} />}
                                            colorScheme="purple"
                                            borderRadius="xl"
                                            w="100%"
                                            onClick={() => handleWatchVideo(tuto.youtube_link, tuto.title)}
                                            shadow="md"
                                        >
                                            {t("Regarder")}
                                        </Button>
                                    </CardFooter>
                                </Card>
                            );
                        })}
                    </SimpleGrid>

                    {/* Pagination */}
                    <Flex justify="space-between" align="center" w="100%">
                        <Box flex="1">
                            <MyPagination
                                paginationData={paginationData}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                PAGE_SIZE={pageSize}
                            />
                        </Box>
                    </Flex>
                </Stack>
            ) : (
                <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    p={10}
                    bg={cardBg}
                    borderRadius="2xl"
                    shadow="sm"
                >
                    <Text fontSize="lg" color="gray.500" mb={2}>
                        {t("Aucun tutoriel disponible")}
                    </Text>
                    <Text fontSize="sm" color="gray.400">
                        {t("Les vidéos explicatives apparaîtront ici dès que l'administrateur les publiera.")}
                    </Text>
                </Flex>
            )}

            {/* Video Modal Player */}
            <Modal isOpen={isOpen} onClose={handleCloseModal} size="4xl" isCentered>
                <ModalOverlay />
                <ModalContent bg="black" borderRadius="2xl" overflow="hidden" m={4}>
                    <ModalCloseButton color="white" zIndex={10} bg="blackAlpha.600" borderRadius="full" _hover={{ bg: 'blackAlpha.800' }} />
                    <ModalBody p={0}>
                        {activeVideoId && (
                            <AspectRatio maxW="100%" ratio={16 / 9}>
                                <iframe
                                    title={activeVideoTitle}
                                    src={`https://www.youtube.com/embed/${activeVideoId}?autoplay=1&rel=0`}
                                    allowFullScreen
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                />
                            </AspectRatio>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Description Modal */}
            <Modal isOpen={isDescOpen} onClose={onDescClose} size="lg" isCentered>
                <ModalOverlay />
                <ModalContent borderRadius="2xl" p={2}>
                    <ModalHeader fontSize="xl" fontWeight="bold" borderBottomWidth="1px" borderColor={useColorModeValue('gray.100', 'gray.700')}>
                        {descModalTitle}
                    </ModalHeader>
                    <ModalCloseButton borderRadius="full" />
                    <ModalBody py={4}>
                        <Text fontSize="md" color={textColor} whiteSpace="pre-wrap">
                            {descModalContent}
                        </Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="purple" borderRadius="xl" onClick={onDescClose}>
                            {t("Fermer")}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

const CirclePlayButton = () => {
    return (
        <Flex
            h={14}
            w={14}
            bg="white"
            borderRadius="full"
            align="center"
            justify="center"
            shadow="lg"
            transition="transform 0.2s"
            _hover={{ transform: 'scale(1.1)' }}
        >
            <Icon as={FaPlay} color="purple.600" w={5} h={5} ml={1} />
        </Flex>
    );
};

export default Tutoriels;
